import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Chip,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Tabs,
    Tab,
} from "@heroui/react";
import {
    LuPencil,
    LuChevronDown,
    LuArrowDown,
    LuArrowUp,
    LuPlus,
} from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { FilterData } from "./components";
import { findOptionTitle, formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

const CashTransactionData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("cash-transaction.edit");
    const initialFilters = {
        type: "",
        search: "",
        start_date: "",
        end_date: "",
        master_cash_id: "",
    };

    const table = useDataTable(
        "cash-transaction/data",
        null, // No delete url
        {},
        initialFilters,
    );

    const { filters, filterPanel } = table;
    const { values, setFilter } = filters;
    const { setPending } = filterPanel;

    const { data: masterCashes } = useFetchData("select/master-cash");

    const filterLabels = {
        type: {
            label: "Tipe",
            getValueLabel: (val) =>
                val === "1" ? "Pemasukan" : val === "2" ? "Pengeluaran" : val,
        },
        search: { label: "Pencarian" },
        start_date: {
            label: "Tanggal Mulai",
            getValueLabel: (val) => formatDate(val),
        },
        end_date: {
            label: "Tanggal Akhir",
            getValueLabel: (val) => formatDate(val),
        },
        master_cash_id: {
            label: "Dompet/Kas",
            getValueLabel: (val) => findOptionTitle(masterCashes, val),
        },
    };

    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Tipe", skeletonWidth: "w-24" },
        { name: "Rincian Transaksi", skeletonWidth: "w-64" },
        { name: "Sumber & Tujuan", skeletonWidth: "w-48" },
        { name: "Nominal", skeletonWidth: "w-32", align: "end" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    const columns = [
        { key: "no", label: "No." },
        { key: "date", label: "Tanggal" },
        { key: "type", label: "Tipe" },
        { key: "detail", label: "Rincian Transaksi" },
        { key: "source_dest", label: "Sumber & Tujuan" },
        { key: "amount", label: "Nominal", align: "end" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "date":
                return (
                    <ThemedText size="sm">
                        {formatDate(
                            item.transaction_date,
                            "dd/MM/yyyy HH:mm",
                        )}
                    </ThemedText>
                );
            case "type":
                return (
                    <Chip
                        size="sm"
                        variant="flat"
                        color={item.category?.type?.badge_color}
                    >
                        {item.category?.type?.name}
                    </Chip>
                );
            case "detail":
                return (
                    <div className="flex flex-col gap-1">
                        <ThemedText size="sm" className="font-medium">
                            {item.category?.name || "-"}
                        </ThemedText>
                        {item.description && (
                            <ThemedText
                                size="xs"
                                variant="caption"
                                className="line-clamp-1"
                            >
                                {item.description}
                            </ThemedText>
                        )}
                    </div>
                );
            case "source_dest": {
                const typeId =
                    item.category?.transaction_category_type_id;
                // 1 is Income, 2 is Expense
                const isIncome = String(typeId) === "1";

                const isTransfer =
                    item.relation_table?.startsWith("cash_transfers");

                let sourceName = "-";
                let destName = "-";

                if (isTransfer) {
                    if (isIncome) {
                        sourceName =
                            item.transfer_source?.master_cash?.name ||
                            "Unknown Source";
                        destName = item.master_cash?.name || "-";
                    } else {
                        sourceName = item.master_cash?.name || "-";
                        destName =
                            item.transfer_destination?.master_cash
                                ?.name || "Unknown Dest";
                    }
                } else {
                    // Normal Transaction
                    sourceName = isIncome
                        ? item.party_name || "-"
                        : item.master_cash?.name || "-";

                    destName = isIncome
                        ? item.master_cash?.name || "-"
                        : item.party_name || "-";
                }

                return (
                    <div className="flex flex-col gap-1">
                        <ThemedText size="sm">
                            <span className="text-default-400 text-xs">
                                Sumber:{" "}
                            </span>
                            {sourceName}
                        </ThemedText>
                        <ThemedText size="sm">
                            <span className="text-default-400 text-xs">
                                Tujuan:{" "}
                            </span>
                            {destName}
                        </ThemedText>
                    </div>
                );
            }
            case "amount":
                return (
                    <div className="flex justify-end">
                        <ThemedText size="sm">
                            {formatCurrency(item.amount)}
                        </ThemedText>
                    </div>
                );
            case "actions":
                return (
                    <div className="flex justify-end gap-2">
                        <Tooltip content="Edit">
                            <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                as={Link}
                                to={`/cash-transaction/${item.category?.transaction_category_type_id === 1 ? "in" : "out"}/${item.id}/edit`}
                            >
                                <LuPencil
                                    size={14}
                                    className="text-default-500"
                                />
                            </Button>
                        </Tooltip>
                    </div>
                );
            default:
                return null;
        }
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Cash Flow."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Cash Flow"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Keuangan", href: "/finance" },
                        { label: "Cash Flow" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />

                    {hasPermission("cash-transaction.create") && (
                        <Dropdown placement="bottom-end">
                            <DropdownTrigger>
                                <Button
                                    color="primary"
                                    endContent={<LuChevronDown />}
                                    startContent={<LuPlus />}
                                    size="sm"
                                >
                                    Buat Transaksi
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Create Actions">
                                <DropdownItem
                                    key="create-in"
                                    as={Link}
                                    to="/cash-transaction/in/create"
                                    description="Buat catatan pemasukan"
                                    startContent={
                                        <LuArrowDown
                                            className="text-success mr-1"
                                            size={18}
                                        />
                                    }
                                >
                                    Pemasukan
                                </DropdownItem>
                                <DropdownItem
                                    key="create-out"
                                    as={Link}
                                    to="/cash-transaction/out/create"
                                    description="Buat catatan pengeluaran"
                                    startContent={
                                        <LuArrowUp
                                            className="text-danger mr-1"
                                            size={18}
                                        />
                                    }
                                >
                                    Pengeluaran
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    )}
                </div>
            </div>

            {!table.filterPanel.isOpen && (
                <div className="flex items-center">
                    <Tabs
                        aria-label="Tipe Transaksi"
                        selectedKey={String(values.type || "all")}
                        onSelectionChange={(key) => {
                            const val = key === "all" ? "" : key;
                            setFilter("type", val);
                            setPending((prev) => ({
                                ...prev,
                                type: val,
                            }));
                        }}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="all" title="Semua Data" />
                        <Tab key="1" title="Pemasukan" />
                        <Tab key="2" title="Pengeluaran" />
                    </Tabs>
                </div>
            )}

            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{ masterCashes: masterCashes || [] }}
            />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada data Cash Flow."
                            }
                        />
                    ) : (
                        <Table aria-label="Cash Flow Table" shadow="none">
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default CashTransactionData;
